import type { ValidationResponse } from '@kk/api/modules/orchestration';
import {
  showToast,
  type ToastNotificationProps,
} from '@kk/ui/components/Notifications/ToastProvider';
import i18n from '@kk/ui/i18n';
import { type ErrorResponse, isErrorResponse } from './api-types';
import { loginRequest } from './auth/auth.config';
import msalInstance from './auth/msalInstance';

/**
 * Represents an error related to missing permissions.
 */
export class PermissionError extends Error {
  constructor(id: string, ...args) {
    super(`Missing permission: "${id}"`, ...args);
  }
}

/**
 * Displays an error toast notification for API errors.
 *
 * @param {Omit<ToastNotificationProps, 'type' | 'headline'>} [toast] - Optional toast configuration, excluding 'type' and 'headline'.
 */
function showAPIErrorToast(
  toast?: Omit<ToastNotificationProps, 'type' | 'headline'>,
) {
  showToast({
    type: 'danger',
    headline: i18n.t('common:error.api.title'),
    ...toast,
  });
}

/**
 * Displays a validation error toast notification.
 *
 * @param {Omit<ToastNotificationProps, 'type' | 'headline'>} [toast] - Optional additional properties to pass to the toast notification.
 */
function showValidationErrorToast(
  toast?: Omit<ToastNotificationProps, 'type' | 'headline'>,
) {
  showToast({
    type: 'danger',
    headline: i18n.t('common:error.validation'),
    message: '',
    ...toast,
  });
}

/**
 * Checks if the provided data is a ValidationResponse object.
 *
 * @param data - The data to be checked for ValidationResponse object type.
 * @returns A boolean indicating whether the data is a ValidationResponse object.
 */
function isValidationResponse(data: unknown): data is ValidationResponse {
  if (!data) return false;
  if (typeof data !== 'object') return false;

  if ('validationMessage' in data || 'validationError' in data) {
    return true;
  }

  return false;
}

/**
 * Checks if the provided data is an array of ValidationResponse objects.
 *
 * @param data - The data to be checked for an array of ValidationResponse objects.
 * @returns A boolean indicating whether the data is an array of ValidationResponse objects.
 */
export function isValidationResponseArray(
  data: unknown,
): data is ValidationResponse[] {
  return Array.isArray(data) && data.every(isValidationResponse);
}

/**
 * Handles error notifications for API responses.
 *
 * @param {unknown} error - The error object that was thrown.
 * @param {string} [key] - An optional key to identify the notification.
 */
export async function handleErrorNotification(
  error?: ErrorResponse<unknown>,
  key?: string,
) {
  const operationId = error ? getOperationId(error) : undefined;
  const operationIdString = operationId ? ` (OperationId: ${operationId})` : '';
  if (isErrorResponse(error)) {
    let message = (await error.text?.()) || error.data;

    if (isValidationResponseArray(message) || isValidationResponse(message)) {
      const validationResponses = Array.isArray(message) ? message : [message];
      showValidationErrorToast({
        message: validationResponses
          .map((m) => m.validationMessage)
          .join('\n')
          .concat(operationIdString),
        key,
      });
      return;
    }

    if (typeof message === 'string') {
      message = message.concat(operationIdString);
      showValidationErrorToast({ message, key });
      return;
    }

    showAPIErrorToast({
      key,
      message: i18n.t('common:error.api.message') + operationIdString,
    });
  }
}

/**
 * Handles token errors by clearing the cache and initiating a logout redirect.
 * @param {unknown} error - The error to be handled.
 */
export function handleTokenError(_error: unknown) {
  msalInstance.clearCache();
  msalInstance.loginRedirect(loginRequest);
}

export function getOperationId(error: unknown): string | undefined {
  if (error instanceof Error && error.cause) {
    const operationIds = (error.cause as { operationIds?: string[] })
      .operationIds;
    if (Array.isArray(operationIds) && operationIds.length > 0) {
      return operationIds.join(', ');
    }
  }
  if (isErrorResponse(error)) {
    return error.headers.get('Operationid') ?? undefined;
  }
}
